import React, { useState, useEffect } from 'react';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { Card, CardContent } from '@/components/ui/card';
import { Separator } from '@/components/ui/separator';
import { MapPin, CreditCard, DollarSign, Clock } from 'lucide-react';

const CheckoutModal = ({ 
  isOpen, 
  onClose, 
  cart, 
  vendor, 
  userProfile,
  onPlaceOrder 
}) => {
  const [deliveryAddress, setDeliveryAddress] = useState({
    street: '',
    apartment: '',
    city: '',
    state: '',
    zip_code: '',
    delivery_instructions: ''
  });
  
  const [paymentMethod, setPaymentMethod] = useState('card');
  const [isLoading, setIsLoading] = useState(false);

  // Pre-fill address from user profile
  useEffect(() => {
    if (userProfile && isOpen) {
      setDeliveryAddress({
        street: userProfile.address || '',
        apartment: userProfile.apartment || '',
        city: userProfile.city || '',
        state: userProfile.state || '',
        zip_code: userProfile.zip_code || '',
        delivery_instructions: ''
      });
    }
  }, [userProfile, isOpen]);

  // Calculate pricing
  const subtotal = cart.reduce((sum, item) => sum + item.subtotal, 0);
  const deliveryFee = vendor?.delivery_fee || 4.99;
  const tax = subtotal * 0.08; // 8% tax
  const total = subtotal + deliveryFee + tax;

  // Calculate estimated delivery time
  const getEstimatedDeliveryTime = () => {
    const prepTime = vendor?.avg_prep_time || vendor?.avg_delivery_time || 30;
    const deliveryBuffer = Math.floor(Math.random() * 11) + 15; // 15-25 minutes
    const totalMinutes = prepTime + deliveryBuffer;
    const estimatedTime = new Date(Date.now() + totalMinutes * 60000);
    return estimatedTime.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' });
  };

  const handlePlaceOrder = async () => {
    // Validate address
    if (!deliveryAddress.street || !deliveryAddress.city || !deliveryAddress.state || !deliveryAddress.zip_code) {
      alert('Please fill in all required address fields');
      return;
    }

    setIsLoading(true);
    
    const orderData = {
      vendor_id: vendor.id,
      items: cart.map(item => ({
        meal_id: item.id || item.meal_id,
        meal_name: item.name || item.meal_name,
        quantity: item.quantity,
        price: item.price,
        subtotal: item.subtotal
      })),
      delivery_address: {
        street: deliveryAddress.street,
        apartment: deliveryAddress.apartment,
        city: deliveryAddress.city,
        state: deliveryAddress.state,
        zip_code: deliveryAddress.zip_code,
        delivery_instructions: deliveryAddress.delivery_instructions
      },
      payment_method: paymentMethod,
      special_instructions: deliveryAddress.delivery_instructions
    };

    try {
      await onPlaceOrder(orderData);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Checkout</DialogTitle>
          <DialogDescription>Complete your order from {vendor?.name || vendor?.business_name}</DialogDescription>
        </DialogHeader>

        <div className="space-y-6">
          {/* Order Summary */}
          <Card>
            <CardContent className="p-4">
              <h3 className="font-semibold mb-3">Order Summary</h3>
              <div className="space-y-2">
                {cart.map((item, idx) => (
                  <div key={idx} className="flex justify-between text-sm">
                    <span>{item.quantity}x {item.name || item.meal_name}</span>
                    <span className="font-medium">${item.subtotal.toFixed(2)}</span>
                  </div>
                ))}
              </div>
              
              <Separator className="my-3" />
              
              <div className="space-y-2 text-sm">
                <div className="flex justify-between">
                  <span>Subtotal</span>
                  <span>${subtotal.toFixed(2)}</span>
                </div>
                <div className="flex justify-between">
                  <span>Delivery Fee</span>
                  <span>${deliveryFee.toFixed(2)}</span>
                </div>
                <div className="flex justify-between">
                  <span>Tax (8%)</span>
                  <span>${tax.toFixed(2)}</span>
                </div>
                <Separator className="my-2" />
                <div className="flex justify-between font-bold text-lg">
                  <span>Total</span>
                  <span className="text-indigo-600">${total.toFixed(2)}</span>
                </div>
              </div>

              <div className="flex items-center gap-2 mt-4 text-sm text-gray-600">
                <Clock className="w-4 h-4" />
                <span>Estimated delivery: {getEstimatedDeliveryTime()}</span>
              </div>
            </CardContent>
          </Card>

          {/* Delivery Address */}
          <Card>
            <CardContent className="p-4">
              <div className="flex items-center gap-2 mb-4">
                <MapPin className="w-5 h-5 text-indigo-600" />
                <h3 className="font-semibold">Delivery Address</h3>
              </div>
              
              <div className="grid gap-4">
                <div>
                  <Label htmlFor="street">Street Address *</Label>
                  <Input
                    id="street"
                    value={deliveryAddress.street}
                    onChange={(e) => setDeliveryAddress({...deliveryAddress, street: e.target.value})}
                    placeholder="123 Main Street"
                    required
                  />
                </div>
                
                <div>
                  <Label htmlFor="apartment">Apartment / Unit (Optional)</Label>
                  <Input
                    id="apartment"
                    value={deliveryAddress.apartment}
                    onChange={(e) => setDeliveryAddress({...deliveryAddress, apartment: e.target.value})}
                    placeholder="Apt 4B"
                  />
                </div>
                
                <div className="grid grid-cols-3 gap-4">
                  <div>
                    <Label htmlFor="city">City *</Label>
                    <Input
                      id="city"
                      value={deliveryAddress.city}
                      onChange={(e) => setDeliveryAddress({...deliveryAddress, city: e.target.value})}
                      placeholder="New York"
                      required
                    />
                  </div>
                  
                  <div>
                    <Label htmlFor="state">State *</Label>
                    <Input
                      id="state"
                      value={deliveryAddress.state}
                      onChange={(e) => setDeliveryAddress({...deliveryAddress, state: e.target.value})}
                      placeholder="NY"
                      maxLength={2}
                      required
                    />
                  </div>
                  
                  <div>
                    <Label htmlFor="zip">Zip Code *</Label>
                    <Input
                      id="zip"
                      value={deliveryAddress.zip_code}
                      onChange={(e) => setDeliveryAddress({...deliveryAddress, zip_code: e.target.value})}
                      placeholder="10001"
                      required
                    />
                  </div>
                </div>
                
                <div>
                  <Label htmlFor="instructions">Delivery Instructions (Optional)</Label>
                  <Textarea
                    id="instructions"
                    value={deliveryAddress.delivery_instructions}
                    onChange={(e) => setDeliveryAddress({...deliveryAddress, delivery_instructions: e.target.value})}
                    placeholder="e.g., Leave at front door, Ring bell twice"
                    rows={2}
                  />
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Payment Method */}
          <Card>
            <CardContent className="p-4">
              <div className="flex items-center gap-2 mb-4">
                <CreditCard className="w-5 h-5 text-indigo-600" />
                <h3 className="font-semibold">Payment Method</h3>
              </div>
              
              <RadioGroup value={paymentMethod} onValueChange={setPaymentMethod}>
                <div className="flex items-center space-x-2 p-3 border rounded-lg hover:bg-gray-50 cursor-pointer">
                  <RadioGroupItem value="card" id="card" />
                  <Label htmlFor="card" className="flex items-center gap-2 cursor-pointer flex-1">
                    <CreditCard className="w-4 h-4" />
                    <div>
                      <p className="font-medium">Credit / Debit Card</p>
                      <p className="text-sm text-gray-500">Pay now (Mock payment for MVP)</p>
                    </div>
                  </Label>
                </div>
                
                <div className="flex items-center space-x-2 p-3 border rounded-lg hover:bg-gray-50 cursor-pointer">
                  <RadioGroupItem value="cash_on_delivery" id="cash" />
                  <Label htmlFor="cash" className="flex items-center gap-2 cursor-pointer flex-1">
                    <DollarSign className="w-4 h-4" />
                    <div>
                      <p className="font-medium">Cash on Delivery</p>
                      <p className="text-sm text-gray-500">Pay when you receive your order</p>
                    </div>
                  </Label>
                </div>
              </RadioGroup>
              
              {paymentMethod === 'card' && (
                <div className="mt-4 p-3 bg-blue-50 border border-blue-200 rounded-lg">
                  <p className="text-sm text-blue-800">
                    <strong>Mock Payment:</strong> This is a demo. No real payment will be processed.
                  </p>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Actions */}
          <div className="flex gap-3">
            <Button
              variant="outline"
              onClick={onClose}
              className="flex-1"
              disabled={isLoading}
            >
              Cancel
            </Button>
            <Button
              onClick={handlePlaceOrder}
              className="flex-1 bg-indigo-600 hover:bg-indigo-700"
              disabled={isLoading}
            >
              {isLoading ? 'Placing Order...' : `Place Order - $${total.toFixed(2)}`}
            </Button>
          </div>

          <p className="text-xs text-gray-500 text-center">
            By placing this order, you agree to our terms and conditions. You can cancel within 5 minutes of placing the order.
          </p>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default CheckoutModal;
